import type { StoreName } from '@bluebrick/discounts-service-module'
import { DateTime } from 'luxon'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { StoreToPortalMap, transactionSpanWrapper } from '@/common/utils'
import { fetchActions } from '@/features/actions/actionsRemoteRepository'
import { type StoreWithActions, type ActionKind, type ActionWithMetadata } from '@/features/actions/types'
import { useEventsStore } from '@/features/events/useEventsStore'
import { emitToParent } from '@/features/iframe/useParentEmit'
import { useSkusStore } from '@/features/skus/useSkusStore'
import { supportsAdvancedOperations } from '@/features/stores/storesUtils'
import { useStoresStore } from '@/features/stores/useStoresStore'
import { useTelemetry } from '@/features/telemetry/useTelemetry'
import { actionsSchema } from '@/schema'
import { type EventData, type Actions, type Portal } from '@/types'

const useActionsInnerStore = defineStore('actionsInner', () => {
	const isLoading = ref(false)
	const actions = ref<Actions>({
		unplannedEvents: [],
		unscheduledEvents: []
	})

	return {
		isLoading,
		actions
	}
})

export const useActionsStore = defineStore('actions', () => {
	const innerStore = useActionsInnerStore()
	const { startTransaction, trackEvent } = useTelemetry()
	const { skusBySkuId } = storeToRefs(useSkusStore())
	const { storeIdToStoreName } = storeToRefs(useStoresStore())
	const { storeEvents } = storeToRefs(useEventsStore())

	const isLoading = computed(() => innerStore.isLoading)
	const actions = computed<Actions>(() => innerStore.actions)
	const actionsFetchAbortController = ref<AbortController>()

	const allActionsCount = computed(() => actions.value.unplannedEvents.length + actions.value.unscheduledEvents.length)

	const sortedActionsByStore = computed<StoreWithActions[]>(() =>
		Object.values(
			[
				...innerStore.actions.unscheduledEvents.map((action) => toActionWithMetadata(action, 'unscheduled')),
				...innerStore.actions.unplannedEvents.map((action) => toActionWithMetadata(action, 'unplanned'))
			]
				.sort((a, b) => {
					// First, compare by store
					if (supportsAdvancedOperations(a.store_name) && !supportsAdvancedOperations(b.store_name)) {
						return -1
					}

					if (!supportsAdvancedOperations(a.store_name) && supportsAdvancedOperations(b.store_name)) {
						return 1
					}

					if (a.store_name !== b.store_name) {
						// Steam always goes first
						if (a.store_name === 'steam') {
							return -1
						}

						if (b.store_name === 'steam') {
							return 1
						}

						return (storeIdToStoreName.value[a.store_name] ?? a.store_name).localeCompare(
							storeIdToStoreName.value[b.store_name] ?? b.store_name
						)
					}

					// Then, compare alphabetically (by SKU human name) within store
					if (a.skuName !== b.skuName) {
						return a.skuName.localeCompare(b.skuName)
					}

					// Lastly, compare by start date
					return a.start_date.toMillis() - b.start_date.toMillis()
				})
				.reduce<Partial<Record<StoreName, StoreWithActions>>>((result, action) => {
					if (action.store_name in result) {
						result[action.store_name]?.actions.push(action)
					} else {
						result[action.store_name] = {
							store: action.store_name,
							name: storeIdToStoreName.value[action.store_name] ?? action.store_name,
							portal: (StoreToPortalMap.get(action.store_name) ?? action.store_name) as Portal,
							actions: [action]
						}
					}

					return result
				}, {})
		)
	)

	async function fetch() {
		if (innerStore.isLoading) {
			actionsFetchAbortController.value?.abort()
		}

		try {
			innerStore.isLoading = true

			actionsFetchAbortController.value = new AbortController()

			const transaction = startTransaction('actions', 'loading')

			trackEvent('app', 'Start loading actions')

			await transactionSpanWrapper(
				async () => {
					let eventData: EventData[] = []
					const start = DateTime.now().minus({ month: 1 })
					const end = DateTime.now().plus({ month: 3 })

					storeEvents.value.forEach((value) => {
						eventData = eventData.concat(
							value.skus.reduce<EventData[]>((acc, sku) => {
								acc = acc.concat(sku.events.filter((event) => event.end_date >= start && event.start_date <= end))

								return acc
							}, [])
						)
					})

					const data = await fetchActions(eventData, actionsFetchAbortController.value?.signal)

					innerStore.actions = actionsSchema.parse(data)
				},
				'summary',
				transaction
			)

			trackEvent('app', 'End loading actions')

			emitToParent('ACTIONS_UPDATED', {
				unplannedEventsCount: innerStore.actions.unplannedEvents.length,
				unscheduledEventsCount: innerStore.actions.unscheduledEvents.length
			})

			transaction?.end()
		} finally {
			innerStore.isLoading = false

			if (actionsFetchAbortController.value?.signal.aborted) {
				// If last fetch was aborted then we need to retry it
				actionsFetchAbortController.value = undefined

				fetch()
			} else {
				actionsFetchAbortController.value = undefined
			}
		}
	}

	function toActionWithMetadata(event: EventData, kind: ActionKind): ActionWithMetadata {
		return {
			...event,
			kind,
			skuName: skusBySkuId.value[event.unique_sku_id]?.human_name ?? event.unique_sku_id
		}
	}

	function indicatePendingRefresh() {
		innerStore.isLoading = true
	}

	return {
		isLoading,
		actions,
		sortedActionsByStore,
		allActionsCount,
		fetch,
		indicatePendingRefresh
	}
})
